import { i18n } from '@fs/zion-locale'
import {
  getActionFieldValue,
  getDisplayLabelForElementType,
  getDisplayLabelForNameType,
  getDisplayLabelForRelType,
  getSlsTypeByValue,
  OP_CODES,
  sortActionsByTimestamp,
} from '@fs/zion-sls'

function getEventDisplayLabelByValue(value, slsTypes) {
  const eventType = getSlsTypeByValue(value, 'events', slsTypes)
  return eventType?.displayLabel || value
}

export function getCreateElementType(action, slsTypes) {
  const { elementType, elementArg } = action

  switch (elementType) {
    case 'RELATIONSHIP':
      return getDisplayLabelForRelType(elementArg.relType, slsTypes)
    case 'FIELD':
      return getActionFieldValue(action)
    case 'NAME':
      return getDisplayLabelForNameType(elementArg.nameType, slsTypes)
    case 'EVENT':
      return getEventDisplayLabelByValue(elementArg.eventType, slsTypes)
    case 'PERSON':
      return elementArg.primary
        ? i18n.t('changes.person.primary', 'PRIMARY')
        : i18n.t('changes.person.secondary', 'SECONDARY')
    default:
      return getDisplayLabelForElementType(elementType, slsTypes)
  }
}

/**
 * Retrieves the change values based on the provided action and elements.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.action - The action object containing details of the operation.
 * @param {Object} params.slsTypes -The SLS types object.
 * @returns {string} The changed value based on the action and elements.
 */
export function getChangeValues({ action, slsTypes }) {
  let value
  switch (action.opCode) {
    case OP_CODES.ADD_FV:
      if (action.fieldValueArg?.normalizedValues) {
        value = action.fieldValueArg.normalizedValues[0].text
      }
      if (action.fieldValueArg?.origValue) {
        value = action.fieldValueArg.origValue.text
      }
      return value
    case OP_CODES.CREATE_ELEMENT:
      value = getCreateElementType(action, slsTypes)

      return value

    default:
      value = Array.isArray(action.opCode) ? action.opCode.join(' ') : action.opCode
      return value
  }
}

// Filter functions for getting person actions history
const justActionWithIdsAndDisplayLabelFilter = (el) => Boolean(el.actionId) && Boolean(el.displayLabel)
const justPersonalActionsFilter = (el, person) => el.personIds?.includes(person?.id)
const rectangleChangesFilter = (el) => !['ADD_RECT', 'REM_RECT'].includes(el?.opCode)
const noAttachmentsFilter = (el) => el.opCode !== 'ADD_SUB'
const createActionWithNoFieldValuesFilter = (el) => {
  if (el?.opCode === 'CREATE_ELEMENT' && !el?.elementArg?.fieldValues?.length) return false
  return true
}

/**
 * Retrieves a filtered history of actions related to a specific person ordered by timestamp (Most recent first).
 *
 * Filter criteria:
 * - Just return actions related to the person.
 * - No actions with no field values.
 * - No rectangle changes.
 * - No attachments.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Array} params.actions - The image actions.
 * @param {Object} params.person - The person object.
 * @returns {Array} An array of filtered and displayable actions related to the person.
 */
export function getPersonActionsHistory({ actions = [], person }) {
  const sortedActions = sortActionsByTimestamp(actions)

  const displayableChanges = sortedActions?.filter(
    (action) =>
      justActionWithIdsAndDisplayLabelFilter(action) &&
      justPersonalActionsFilter(action, person) &&
      rectangleChangesFilter(action) &&
      noAttachmentsFilter(action) &&
      createActionWithNoFieldValuesFilter(action)
  )

  return displayableChanges
}

/**
 * Converts a timestamp to a formatted date string based on the current language setting.
 *
 * @param {number|string} timestamp - The timestamp to be converted. Can be a number (milliseconds since epoch) or a string.
 * @returns {string} The formatted date string in the format "Month Day, Year" (e.g., "January 1, 2023").
 */
export function converTimestampToDate(timestamp) {
  const date = new Date(timestamp)
  return date.toLocaleString(i18n.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}
